import React, { useCallback, useState, useRef, useEffect } from 'react';
import {
  Input,
  Label,
  makeStyles,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';
import { useI18nContext } from '@bingads-webui/copilot-i18n';
import { dispatchStructuredLogUserEngagementEvent } from '@bingads-webui-react/copilot-logger';
import { type ILogger } from '@bingads-webui-react/msa-copilot-common';

const useCaptchaStyles = makeStyles({
  root: {
    marginTop: tokens.spacingVerticalM,
    display: 'inline-flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusXLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  titleLabel: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  subtitleLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  captchaFrame: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  controls: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase200,
  },
  controlLink: {
    cursor: 'pointer',
    color: tokens.colorBrandForeground1,
    textDecorationLine: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    '&:hover': {
      textDecorationLine: 'underline',
    },
  },
  disabledLink: {
    opacity: 0.4,
    cursor: 'not-allowed',
    pointerEvents: 'none' as const,
  },
  separator: {
    color: tokens.colorNeutralStroke1,
  },
  canvas: {
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: '#f5f5f0',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    width: '100%',
    height: 'auto',
  },
  answerInput: {
    width: '100%',
    borderRadius: tokens.borderRadiusLarge,
    '&:hover': {
      borderColor: tokens.colorBrandStroke1,
    },
    '&:focus-within': {
      outline: `0.5px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '-1px',
      borderRadius: tokens.borderRadiusLarge,
    },
    '&:focus-within::after': {
      display: 'none',
    },
    '& > *::after': {
      display: 'none !important',
    },
  },
  errorText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
});

/* Characters that are visually distinct and hard to confuse */
const CAPTCHA_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
const CAPTCHA_LENGTH = 6;

/* Generate a random captcha string */
const generateCaptchaText = (): string => {
  let text = '';
  for (let i = 0; i < CAPTCHA_LENGTH; i += 1) {
    text += CAPTCHA_CHARS.charAt(Math.floor(Math.random() * CAPTCHA_CHARS.length));
  }
  return text;
};

/* Draw distorted text on a canvas */
const drawCaptcha = (canvas: HTMLCanvasElement, text: string) => {
  try {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { width } = canvas;
    const { height } = canvas;

    /* Clear canvas */
    ctx.fillStyle = '#f5f5f0';
    ctx.fillRect(0, 0, width, height);

    /* Draw noise lines */
    for (let i = 0; i < 4; i += 1) {
      ctx.strokeStyle = `rgba(${(Math.random() * 100) + 100}, ${(Math.random() * 100) + 100}, ${(Math.random() * 100) + 150}, 0.4)`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(Math.random() * width, Math.random() * height);
      ctx.bezierCurveTo(
        Math.random() * width, Math.random() * height,
        Math.random() * width, Math.random() * height,
        Math.random() * width, Math.random() * height
      );
      ctx.stroke();
    }

    /* Draw each character with random rotation and position */
    const charWidth = width / (text.length + 1);
    for (let i = 0; i < text.length; i += 1) {
      ctx.save();
      const x = (charWidth * (i + 0.5)) + ((Math.random() * 10) - 5);
      const y = (height / 2) + ((Math.random() * 12) - 6);
      const rotation = (Math.random() - 0.5) * 0.6;
      const fontSize = 24 + Math.floor(Math.random() * 6);

      ctx.translate(x, y);
      ctx.rotate(rotation);
      ctx.font = `${fontSize}px serif`;
      ctx.fillStyle = `rgba(${Math.floor(Math.random() * 80)}, ${Math.floor(Math.random() * 80)}, ${Math.floor((Math.random() * 120) + 60)}, 0.9)`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text[i], 0, 0);
      ctx.restore();
    }

    /* Draw noise dots */
    for (let i = 0; i < 30; i += 1) {
      ctx.fillStyle = `rgba(${Math.random() * 200}, ${Math.random() * 200}, ${Math.random() * 200}, 0.3)`;
      ctx.beginPath();
      ctx.arc(Math.random() * width, Math.random() * height, 1, 0, Math.PI * 2);
      ctx.fill();
    }
  } catch {
    /* Canvas drawing may fail in test environments (jsdom) — silently ignore */
  }
};

export interface CaptchaVerificationProps {
  onVerified: () => void;
  onBotDetected?: () => void;
  disabled?: boolean;
  logger?: ILogger;
  conversationId?: string;
}

export const CaptchaVerification: React.FC<CaptchaVerificationProps> = ({
  onVerified,
  onBotDetected,
  disabled = false,
  logger,
  conversationId,
}) => {
  const [captchaText, setCaptchaText] = useState(generateCaptchaText());
  const [userAnswer, setUserAnswer] = useState('');
  const [hasError, setHasError] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const showTimeRef = useRef(Date.now());
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const styles = useCaptchaStyles();
  const i18n = useI18nContext();

  const getString = (str: string) => (i18n ? i18n.getString(str) : str);

  interface CaptchaEventData {
    action: string;
    attempts?: number;
    timeToCompleteMs?: number;
  }

  const dispatchCaptchaEvent = useCallback((eventData: CaptchaEventData) => {
    dispatchStructuredLogUserEngagementEvent({
      activity: {
        conversation: { id: conversationId },
      } as any,
      options: {
        captchaEvent: {
          ...eventData,
          conversationId,
        },
      },
    });
  }, [conversationId]);

  useEffect(() => {
    showTimeRef.current = Date.now();
    dispatchCaptchaEvent({ action: 'shown' });
    logger?.trace(`Copilot - CAPTCHA: Shown. conversationId=${conversationId}`);
  }, [dispatchCaptchaEvent, logger, conversationId]);

  /* Draw captcha whenever text changes */
  useEffect(() => {
    if (canvasRef.current) {
      drawCaptcha(canvasRef.current, captchaText);
    }
  }, [captchaText]);

  const handleRefresh = useCallback(() => {
    setCaptchaText(generateCaptchaText());
    setUserAnswer('');
    setHasError(false);
    showTimeRef.current = Date.now();
    dispatchCaptchaEvent({ action: 'refreshed' });
    logger?.trace(`Copilot - CAPTCHA: Refreshed. conversationId=${conversationId}`);
  }, [dispatchCaptchaEvent, logger, conversationId]);

  const handleAudio = useCallback(() => {
    try {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        const spaced = captchaText.split('').join(', ');
        const utterance = new SpeechSynthesisUtterance(spaced);
        utterance.rate = 0.7;
        window.speechSynthesis.speak(utterance);
      }
      dispatchCaptchaEvent({ action: 'audio_played' });
      logger?.trace(`Copilot - CAPTCHA: Audio played. conversationId=${conversationId}`);
    } catch (e) {
      logger?.error(`Copilot - CAPTCHA: audio playback failed: ${JSON.stringify(e)}.`);
    }
  }, [captchaText, dispatchCaptchaEvent, logger, conversationId]);

  const handleInputChange = useCallback((_ev: any, data: { value: string }) => {
    setUserAnswer(data.value);
    setHasError(false);
  }, []);

  const handleSubmit = useCallback(() => {
    const newAttemptCount = attemptCount + 1;
    setAttemptCount(newAttemptCount);
    const timeToComplete = Date.now() - showTimeRef.current;

    if (userAnswer.toLowerCase() === captchaText.toLowerCase()) {
      dispatchCaptchaEvent({ action: 'verified', attempts: newAttemptCount, timeToCompleteMs: timeToComplete });
      logger?.trace(`Copilot - CAPTCHA: Verified. attempts=${newAttemptCount}, timeMs=${timeToComplete}, conversationId=${conversationId}`);
      onVerified();
    } else {
      dispatchCaptchaEvent({ action: 'failed', attempts: newAttemptCount });
      logger?.trace(`Copilot - CAPTCHA: Failed attempt ${newAttemptCount}. conversationId=${conversationId}`);
      setHasError(true);
      setCaptchaText(generateCaptchaText());
      setUserAnswer('');
      showTimeRef.current = Date.now();
      onBotDetected?.();
    }
  }, [userAnswer, captchaText, onVerified, onBotDetected, attemptCount, dispatchCaptchaEvent, logger, conversationId]);

  const handleKeyDown = useCallback((ev: React.KeyboardEvent) => {
    if (ev.key === 'Enter' && userAnswer) {
      handleSubmit();
    }
  }, [userAnswer, handleSubmit]);

  return (
    <div className={mergeClasses('captcha-verification', styles.root)}>
      <div className={styles.titleLabel}>
        {getString(_TL_('Verify to continue'))}
      </div>
      <div className={styles.subtitleLabel}>
        {getString(_TL_('Enter the characters to connect with an expert'))}
      </div>
      <div className={styles.captchaFrame}>
        <div className={styles.controls}>
          <span
            className={mergeClasses('captcha-new-link', styles.controlLink)}
            role="button"
            tabIndex={0}
            onClick={handleRefresh}
            onKeyDown={(e) => { if (e.key === 'Enter') handleRefresh(); }}
          >
            {getString(_TL_('New'))}
          </span>
          <span className={styles.separator}>|</span>
          <span
            className={mergeClasses('captcha-audio-link', styles.controlLink)}
            role="button"
            tabIndex={0}
            onClick={handleAudio}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAudio(); }}
          >
            {getString(_TL_('Audio'))}
          </span>
        </div>
        <canvas
          ref={canvasRef}
          className={mergeClasses('captcha-canvas', styles.canvas)}
          width={220}
          height={65}
          aria-label={getString(_TL_('CAPTCHA image'))}
        />
        <Input
          className={mergeClasses('captcha-answer-input', styles.answerInput)}
          type="text"
          autoComplete="off"
          value={userAnswer}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={getString(_TL_('Enter the characters you see'))}
          aria-label={getString(_TL_('Enter the characters you see'))}
        />
        {hasError && (
          <Label className={styles.errorText}>
            {getString(_TL_('The characters you entered are incorrect. Please try again.'))}
          </Label>
        )}
        <span
          className={mergeClasses('captcha-submit-link', styles.controlLink, (!userAnswer || disabled) ? styles.disabledLink : '')}
          role="button"
          tabIndex={0}
          onClick={userAnswer && !disabled ? handleSubmit : undefined}
          onKeyDown={(e) => { if (e.key === 'Enter' && userAnswer && !disabled) handleSubmit(); }}
          aria-disabled={!userAnswer || disabled}
        >
          {getString(_TL_('Submit'))}
        </span>
      </div>
    </div>
  );
};
